import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { toast } from '@grandvisionhq/common';
import { useMutation } from '@grandvisionhq/graphql';
import { useCartStorage } from '@grandvisionhq/lib-utils-cart';
import { useDispatchTrackEvent, useDispatchTrackErrorEvent } from '@grandvisionhq/svc-tealium/events';
import { isNil } from '@grandvisionhq/utils';
import { mapLineItemToDataLayerEvent } from '../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import '@grandvisionhq/payments';
import { mapRemovedProductToTealiumProduct, mapLineItemProductToTealiumProduct } from '../mappers/analytics/tealium/line-item-to-tealium-product.js';
import '../operations/fragments/cart-details.js';
import '../operations/fragments/cart-validation-errors.js';
import '../operations/fragments/recover-inactive-cart-failure.js';
import '../operations/fragments/recover-inactive-cart-result.js';
import '../operations/mutations/add-cart-line-items.js';
import '../operations/mutations/add-cart-voucher-codes.js';
import '../operations/mutations/create-cart.js';
import '../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../operations/mutations/create-cart-from-order.js';
import '../operations/mutations/duplicate-cart-line-items.js';
import '../operations/mutations/remove-cart-voucher-codes.js';
import '../operations/mutations/remove-cart-line-items.js';
import { UPDATE_CART_LINE_ITEMS } from '../operations/mutations/update-cart-line-items.js';
import '../operations/mutations/recover-inactive-cart.js';
import { GET_CART } from '../operations/queries/get-cart.js';
import '../operations/queries/get-total-line-items.js';
import '../operations/queries/get-cart-estimated-delivery.js';
import '../utils/cart-count-var.js';
import '../utils/cart-id-var.js';
import '@grandvisionhq/commercetools';
import { getValidationErrorCodes } from '../utils/get-validation-error-codes.js';
import '../utils/get-unrecoverable-validation-errors.js';
import '../models/line-item/prescription-glasses.js';
import '../utils/get-failure-error-codes.js';

const useUpdateLineItemQuantity = (lineItem1)=>{
    const [{ cartId  }, { setCartCount  }] = useCartStorage();
    const [quantityDiff, setQuantityDiff] = React.useState(0);
    const dispatchTrackEvent = useDispatchTrackEvent();
    const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
    const handleValidationErrors = (validationErrors)=>{
        validationErrors.forEach((validationError)=>toast.failed(`cart.${validationError}`)
        );
    };
    const handleAddToCartTealium = (lineItem)=>{
        const Products = mapLineItemProductToTealiumProduct([
            lineItem
        ]);
        dispatchTrackEvent(()=>Promise.resolve({
                id: 'Event',
                Events_CartAdd: '1',
                Products
            })
        );
    };
    const handleRemoveFromCartTealium = React.useCallback(()=>{
        dispatchTrackEvent(()=>Promise.resolve({
                id: 'Event',
                Events_CartRemove: '1',
                Products: mapRemovedProductToTealiumProduct(lineItem1)
            })
        );
    }, [
        dispatchTrackEvent,
        lineItem1
    ]);
    const [updateCartItems, updateQuantityMutation] = useMutation(UPDATE_CART_LINE_ITEMS, {
        onCompleted: ()=>{
            if (quantityDiff === 0) {
                return;
            }
            const id = cartId || '';
            const eventType = quantityDiff > 0 ? DataLayer.DataLayerTypes.EventType.add : DataLayer.DataLayerTypes.EventType.remove;
            const dataLayerEvents = mapLineItemToDataLayerEvent(eventType, [
                swcHelpers.objectSpread({}, lineItem1, {
                    quantity: Math.abs(quantityDiff)
                })
            ], id);
            dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
            quantityDiff > 0 ? handleAddToCartTealium(swcHelpers.objectSpread({}, lineItem1, {
                quantity: Math.abs(quantityDiff)
            })) : handleRemoveFromCartTealium();
        },
        refetchQueries: [
            'GetCart'
        ]
    });
    const updateItemQuantity = React.useCallback((updatedQuantity)=>{
        if (isNil(cartId)) {
            console.error('No cart ID available');
            dispatchTrackErrorEvent(()=>Promise.resolve({
                    Error_Source: 'Server',
                    Error_Code: 'Update cart line item quantity error',
                    Error_Details: 'No cart ID available'
                })
            );
            return;
        }
        setQuantityDiff(updatedQuantity - lineItem1.quantity);
        updateCartItems({
            variables: {
                cartInput: {
                    cartId,
                    lineItems: [
                        {
                            id: lineItem1.id,
                            quantity: updatedQuantity
                        }, 
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.updateCartLineItems.success === true) {
                    const newCart = data.updateCartLineItems.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: {
                            cart: {
                                cart: newCart,
                                validationErrors: null,
                                failures: null
                            }
                        }
                    });
                    setCartCount((newCart === null || newCart === void 0 ? void 0 : newCart.numberOfItems) || 0);
                }
                if (data && !data.updateCartLineItems.success && data.updateCartLineItems.validationErrors) {
                    const validationErrors = getValidationErrorCodes(data.updateCartLineItems.validationErrors);
                    handleValidationErrors(validationErrors);
                }
            }
        });
    }, [
        cartId,
        dispatchTrackErrorEvent,
        lineItem1.id,
        lineItem1.quantity,
        setCartCount,
        updateCartItems
    ]);
    const { called , loading , error  } = updateQuantityMutation;
    return [
        updateItemQuantity,
        {
            called,
            loading,
            error
        }
    ];
};

export { useUpdateLineItemQuantity };
//# sourceMappingURL=use-update-line-item-quantity.js.map
